import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { getSessionUsage } from "./api";
import type { SessionUsage, TaskEvent } from "./types";

type UsageTab = "overview" | "models" | "tools" | "turns" | "activity";

const TABS: ReadonlyArray<{ id: UsageTab; label: string }> = [
  { id: "overview", label: "概览" },
  { id: "models", label: "按模型" },
  { id: "tools", label: "按工具" },
  { id: "turns", label: "按 Turn" },
  { id: "activity", label: "模型活动" },
];

const TURN_STATUS_LABELS: Record<SessionUsage["turns"][number]["status"], string> = {
  accepted: "已受理",
  running: "执行中",
  waiting_plan: "等待计划",
  waiting_approval: "等待审批",
  completed: "已完成",
  failed: "失败",
  cancelled: "已取消",
};

export function SessionUsageDialog({
  sessionId,
  events,
  onClose,
}: {
  sessionId: string;
  events: readonly TaskEvent[];
  onClose: () => void;
}) {
  const [usage, setUsage] = useState<SessionUsage | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<UsageTab>("overview");
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setUsage(null);
    setError("");
    void getSessionUsage(sessionId)
      .then((result) => {
        if (!cancelled) setUsage(result);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "加载会话用量失败");
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-labelledby="session-usage-title"
        aria-modal="true"
        className="modal-card session-usage-dialog"
        role="dialog"
      >
        <header className="modal-header">
          <div>
            <p className="eyebrow">SESSION USAGE</p>
            <h2 id="session-usage-title">会话用量与工具调用</h2>
          </div>
          <button
            ref={closeRef}
            aria-label="关闭会话用量与工具调用"
            className="icon-button"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </header>

        <nav aria-label="会话用量视图" className="session-usage-tabs" role="tablist">
          {TABS.map((item) => (
            <button
              aria-selected={tab === item.id}
              className={tab === item.id ? "selected" : ""}
              key={item.id}
              role="tab"
              type="button"
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="session-usage-body" role="tabpanel">
          {error && (
            <p className="session-usage-error" role="alert">
              {error}
            </p>
          )}
          {!usage && !error && <p className="muted">正在加载会话用量…</p>}
          {usage && tab === "overview" && <UsageOverview usage={usage} />}
          {usage && tab === "models" && <UsageModels usage={usage} />}
          {usage && tab === "tools" && <UsageTools usage={usage} />}
          {usage && tab === "turns" && <UsageTurns usage={usage} />}
          {tab === "activity" && <UsageActivity events={events} turns={usage?.turns ?? []} />}
        </div>

        <p className="session-usage-note">
          仅统计已入账的已终止调用；进行中的调用完成后出现。token
          不是费用。模型表包含标题生成等会话级调用； 工具表只统计已产生执行回执的动作。
        </p>
      </section>
    </div>
  );
}

function UsageOverview({ usage }: { usage: SessionUsage }) {
  return (
    <div className="usage-breakdown">
      <div className="usage-totals">
        <UsageMetric label="模型调用" value={usage.totals.model_calls} />
        <UsageMetric label="模型输入 token" value={usage.totals.input_tokens} />
        <UsageMetric label="模型输出 token" value={usage.totals.output_tokens} />
        <UsageMetric label="未返回用量的调用" value={usage.totals.unreported_model_calls} />
        <UsageMetric label="工具调用" value={usage.totals.tool_calls} />
        <UsageMetric
          label="工具成功 / 失败"
          text={`${formatCount(usage.totals.succeeded_tool_calls)} / ${formatCount(
            usage.totals.failed_tool_calls,
          )}`}
        />
        <UsageMetric label="厂商搜索执行" value={usage.totals.provider_search_executions} />
        <UsageMetric label="厂商搜索调用" value={usage.totals.provider_search_calls} />
      </div>
      <dl className="session-usage-definitions">
        <div>
          <dt>未返回用量</dt>
          <dd>调用已终止，但服务商没有返回 token 回执；不计为 0。</dd>
        </div>
        <div>
          <dt>模型 token 合计</dt>
          <dd>{formatCount(usage.totals.total_tokens)}</dd>
        </div>
        <div>
          <dt>厂商搜索 token</dt>
          <dd>
            输入 {formatCount(usage.provider_search.input_tokens)} · 输出{" "}
            {formatCount(usage.provider_search.output_tokens)} · 未返回{" "}
            {formatCount(usage.provider_search.unreported_calls)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function UsageModels({ usage }: { usage: SessionUsage }) {
  if (usage.models.length === 0) return <p className="muted">本会话尚无模型调用。</p>;
  return (
    <table className="session-usage-table">
      <thead>
        <tr>
          <th>模型</th>
          <th>调用</th>
          <th>输入</th>
          <th>输出</th>
          <th>合计</th>
          <th>未返回</th>
          <th>最近使用</th>
        </tr>
      </thead>
      <tbody>
        {usage.models.map((model) => (
          <tr key={model.model_id}>
            <td>
              <strong>{model.model_display_name}</strong>
              <small>{model.model_id.slice(0, 12)}…</small>
            </td>
            <td>{formatCount(model.calls)} 次</td>
            <td>{formatCount(model.input_tokens)}</td>
            <td>{formatCount(model.output_tokens)}</td>
            <td>{formatCount(model.total_tokens)}</td>
            <td>
              {model.unreported_calls > 0 ? `${formatCount(model.unreported_calls)} 次` : "—"}
            </td>
            <td>{formatTimestamp(model.last_used_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UsageTools({ usage }: { usage: SessionUsage }) {
  if (usage.tools.length === 0) return <p className="muted">本会话尚无已执行的工具调用。</p>;
  return (
    <table className="session-usage-table">
      <thead>
        <tr>
          <th>Server</th>
          <th>函数</th>
          <th>调用</th>
          <th>成功</th>
          <th>失败</th>
          <th>最近使用</th>
        </tr>
      </thead>
      <tbody>
        {usage.tools.map((tool) => (
          <tr key={`${tool.server_id}\u0000${tool.function_name}`}>
            <td>{tool.server_id}</td>
            <td>{tool.function_name}</td>
            <td>{formatCount(tool.calls)} 次</td>
            <td>{formatCount(tool.succeeded_calls)}</td>
            <td>{formatCount(tool.failed_calls)}</td>
            <td>{formatTimestamp(tool.last_used_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UsageTurns({ usage }: { usage: SessionUsage }) {
  if (usage.turns.length === 0) return <p className="muted">本会话尚无 Turn 记录。</p>;
  return (
    <table className="session-usage-table">
      <thead>
        <tr>
          <th>#</th>
          <th>状态</th>
          <th>时间</th>
          <th>模型调用</th>
          <th>tokens</th>
          <th>未返回</th>
          <th>工具调用</th>
          <th>厂商搜索</th>
        </tr>
      </thead>
      <tbody>
        {usage.turns.map((turn, index) => (
          <tr key={turn.turn_id}>
            <td>{index + 1}</td>
            <td>{TURN_STATUS_LABELS[turn.status]}</td>
            <td>{formatTimestamp(turn.created_at)}</td>
            <td>{formatCount(turn.model_calls)} 次</td>
            <td>{formatCount(turn.total_tokens)}</td>
            <td>{formatCount(turn.unreported_model_calls)}</td>
            <td>
              {formatCount(turn.tool_calls)} 次（{formatCount(turn.succeeded_tool_calls)} 成功 /{" "}
              {formatCount(turn.failed_tool_calls)} 失败）
            </td>
            <td>
              {formatCount(turn.provider_search_executions)} 次 /{" "}
              {formatCount(turn.provider_search_calls)} 搜索
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UsageActivity({
  events,
  turns,
}: {
  events: readonly TaskEvent[];
  turns: SessionUsage["turns"];
}) {
  const activities = events.filter((event) => event.event_type === "model_activity").slice(-200);
  if (activities.length === 0) return <p className="muted">本会话尚无模型活动事件。</p>;
  const turnIndex = new Map(turns.map((turn, index) => [turn.turn_id, index + 1]));
  return (
    <>
      <p className="muted">事件回执，token 以账本表为准。</p>
      <ol className="session-activity-list">
        {activities.map((event) => (
          <li key={event.event_id}>
            <small>
              {event.turn_id && turnIndex.has(event.turn_id)
                ? `第 ${turnIndex.get(event.turn_id)} 轮 · `
                : ""}
              {formatTimestamp(event.timestamp)}
            </small>
            <span>{event.message ?? "模型活动"}</span>
          </li>
        ))}
      </ol>
    </>
  );
}

function UsageMetric({ label, value, text }: { label: string; value?: number; text?: string }) {
  return (
    <div className="usage-metric">
      <span>{label}</span>
      <strong>{text ?? formatCount(value ?? 0)}</strong>
    </div>
  );
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("zh-CN").format(value);
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}
